import { DatadogIconUrl } from "@p0security/integrations-datadog-shared";
import { installedItems } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";
import { camelCase, mapKeys, mapValues } from "lodash-es";

import { datadogMcpTools } from "./mcp-tools.ts";
import { DatadogSites } from "./sites.ts";
import type { DatadogAiIntegration } from "./types.ts";

// Measured on a US5 organization: a new token worked after 2-3s, a scope
// change after 5-8s.
const datadogPropagationHint = propagationHint({
  resource: "Datadog",
  normalWait: "5 to 15 seconds",
  maxWait: "1 minute",
  pollInterval: "5 seconds",
});

const prefixArgs = ["datadog-ai", "permission"];

const accessPrompt = `Request access to a Datadog organization by calling with argv:

  [<organization>, <scope>..., "--reason", <reason>]

where:

  - organization: The Datadog organization, as installed in P0
  - scope: One or more Datadog permission names (e.g. 'monitors_read',
    'logs_read_data'). Always include 'mcp_read', which every tool of this
    server needs, and add the scopes of the tools you intend to call and no
    others. Use the list tool to enumerate them.
  - reason: The reason access is needed

Every tool is listed whether or not your access covers it; Datadog refuses a
call that your scopes do not cover.
`;

const datadogAiMcp = ({
  label,
  domain,
}: {
  label: string;
  domain: string;
}): McpServerSpec & { type: "external" } => ({
  type: "external",
  label: `Datadog (${label})`,
  iconUrl: DatadogIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  tools: { type: "manual", tools: datadogMcpTools },
  // The token's scopes decide what Datadog allows.
  authorization: "upstream",
  url: `https://mcp.${domain}/v1/mcp`,
  access: {
    prompt: accessPrompt,
    prefixArgs,
    propagationHint: datadogPropagationHint,
  },
  list: {
    fields: {
      organization: {
        description: "Datadog organization",
        required: [],
      },
      policies: {
        description:
          "Datadog permission that a token can hold, listed for one organization",
        required: ["organization"],
      },
    },
    prefixArgs,
  },
});

const serverIdFor = (site: string) => camelCase(`datadog-${site}`);

/** One predefined MCP server per Datadog site, keyed e.g. `datadogUs5`. An
 * install adds the server for its organization's site. */
export const datadogAiMcpServers = mapKeys(
  mapValues(DatadogSites, datadogAiMcp),
  (_, site) => serverIdFor(site)
);

const hasInstalledSite =
  (site: string) =>
  (installed: Record<string, NewConfig>): boolean =>
    Object.values(
      installedItems(
        "credential",
        (installed["datadog-ai"] ?? {}) as DatadogAiIntegration
      )
    ).some((item) => item?.site.type === site);

/** Whether each of {@link datadogAiMcpServers} can be added: only when a
 * Datadog organization on its site is installed. */
export const datadogAiMcpServerAvailability = mapKeys(
  mapValues(DatadogSites, (_, site) => hasInstalledSite(site)),
  (_, site) => serverIdFor(site)
);
